import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import {
  PagesMain,
  PagesSelect,
  PagesNav,
  PagesWrapper,
  PageContent,
  PageTitle,
  PagesSelectWrapper,
  SelectTitle,
  SelectDropdown,
  SelectOption
} from './index.style'
import Container from '@components/Container'
import Grid from '@components/Grid'
import GridItem from '@components/GridItem'
import { useState } from 'react'
import Button from '@components/Button'
import Select from '@components/Select'
import Spacer from '@components/Spacer'
import { useEffect } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useRef } from 'react'
import { useStore } from '@Store/'
import debounce from 'lodash.debounce'
import { animation } from '@styles/vars/animation.style'
import IconArrowDown from '@svgs/IconArrowDown'
import { Heading2Serif } from '@components/TextStyles';

gsap.registerPlugin(ScrollTrigger)

const Pages = ({ options, extraLinks }) => {
  const [store] = useStore()
  const [activePage, setActivePage] = useState(options[0].value)
  const [activeLabel, setActiveLabel] = useState(options[0].label)
  const [initialTabSet, setInitialTabSet] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const $main = useRef()
  const $wrapper = useRef()
  const $nextPage = useRef()
  const $pages = useRef()
  const $filtersWrapper = useRef()
  const $filters = useRef()
  const timeline = useRef()
  const newOptions = extraLinks ? options.concat(extraLinks) : options;


  useEffect(() => {
    $nextPage.current = document.querySelector(`[data-value="${activePage}"]`)
    $pages.current = document.querySelectorAll('[data-value]')
    for (const box of $pages.current) {
      box.style.display = 'none';
    }
    $nextPage.current.style.display = "block";
  }, [activePage])

  const updatePage = (tab, label) => {

    if(tab !== 'data-processing-agreement') {
      window.scrollTo({
        top: $main.current.offsetTop,
        left: 0,
        behavior: "smooth",
      });
      window.history.replaceState(
        null,
        '',
        `${window.location.origin}${window.location.pathname}?tab=${tab}`
      )

      setActivePage(tab)
    }else {
      window.location.href= '/' + tab
    }
    setActiveLabel(label)
    setShowDropdown(false)
  }

  const setInitialPage = useCallback(
    tab => {
      if (tab === activePage) return

      if (store.smoothScroll) {
        gsap.to(store.smoothScroll, {
          scrollTop: store.smoothScroll.offset($main.current),
          duration: 1,
          onComplete: () => setInitialTabSet(true),
        })
      }

      setActivePage(tab)
    },
    [store.smoothScroll, activePage]
  )

  useEffect(() => {
    if (initialTabSet) return

    const queryString = window.location.search,
      urlParams = new URLSearchParams(queryString)

    if (queryString && urlParams.get('tab')) {
      setTimeout(() => {
        setInitialPage(urlParams.get('tab'))
      }, animation.maskHideDuration)
    }
  }, [setInitialPage, initialTabSet])

  useEffect(() => {
    window.addEventListener('changeTab', () => {
      const queryString = window.location.search,
        urlParams = new URLSearchParams(queryString)

      if (queryString && urlParams.get('tab')) {
        setTimeout(() => {
          setInitialPage(urlParams.get('tab'))
        }, animation.maskHideDuration)
      }
      setActivePage(urlParams.get('tab'))
    })
  }, [])

  return (
    <PagesMain ref={$main}>
      <div id="mainSectionTrigger"></div>
      <Container>
        <Grid>
          <GridItem ref={$filtersWrapper} tabletL={5} desk={4}>
            <PagesSelectWrapper>
              <PagesSelect>
                <SelectTitle onClick={() => setShowDropdown(!showDropdown)}><span>{activeLabel}</span><IconArrowDown responsive={false} /></SelectTitle>
                <SelectDropdown show={showDropdown}>
                {React.Children.toArray(
                  newOptions.map(option => (
                    <SelectOption onClick={() => updatePage(option.value, option.label)} disabled={activePage === option.value}>{option.label}</SelectOption>
                  ))
                )}
                </SelectDropdown>
              </PagesSelect>

              <PagesNav ref={$filters}>
                {React.Children.toArray(
                  newOptions.map(option => (
                    <Button
                      variant="outline"
                      onClick={() => updatePage(option.value, option.label)}
                      disabled={activePage === option.value}
                    >
                      {option.label}
                    </Button>
                  ))
                )}
              </PagesNav>
            </PagesSelectWrapper>
          </GridItem>

          <GridItem tabletL={9} desk={10}>
            <PagesWrapper ref={$wrapper}>
              {React.Children.toArray(
                options.map(page => (
                  <PageContent
                    show={page.value === activePage}
                    aria-hidden={page.value !== activePage}
                    data-value={page.value}
                  >
                    <Heading2Serif>{page.label}</Heading2Serif>

                    <Spacer size={[20, 40]} />

                    {page.content}

                    <Spacer size={[40, 80]} />
                  </PageContent>
                ))
              )}
            </PagesWrapper>
          </GridItem>
        </Grid>
      </Container>

      <Spacer size={[80, 160]} />
    </PagesMain>
  )
}

Pages.propTypes = {
  options: PropTypes.arrayOf(PropTypes.object),
}

export default Pages
